import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Modal from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import useModalState from '@/hooks/pure/useModalState';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import { IMPORT_STATE, type OkendoInfo } from '../types';
import OkendoTokenModal from './OkendoTokenModal';
import { useOkendo } from './useOkendo';

export const OkendoItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const {
    okendoWorkspace,
    disconnectOkendoMutation,
    requestImportMutation,
    setOkendoWorkspace,
  } = useOkendo(!isConnector);

  const tokenModalState = useModalState();
  const importModalState = useModalState<{ isNew: boolean }>();

  const handleConnectComplete = (_workspace: OkendoInfo) => {
    if (!currWorkspace) return;

    setOkendoWorkspace(_workspace);
    tokenModalState.close();
  };

  const handleDisconnect = () => {
    disconnectOkendoMutation();
  };

  const handleImport = (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!okendoWorkspace}
        source={SOURCE_TYPE.OKENDO}
        description="Import reviews as feedback into Syncly."
        connectedDisplay={'Okendo'}
        info={okendoWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => tokenModalState.open()}
        additionalActions={(workspace) =>
          workspace.import_state === IMPORT_STATE.IMPORTING ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <Typo type="body2" className="text-primary">
                Importing
              </Typo>
            </div>
          ) : (
            <Button
              variant="outlined"
              onClick={() => importModalState.open({ modalState: { isNew: false } })}
            >
              One-time import
            </Button>
          )
        }
      />
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import Reviews'}
          subTitle={'Okendo'}
          historyList={okendoWorkspace?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImport({ start_date, end_date })
          }
        />
      </Modal>
      <Modal
        status={tokenModalState.status}
        closeModal={tokenModalState.close}
        useBackgroundClose
      >
        <OkendoTokenModal onConnectComplete={handleConnectComplete} />
      </Modal>
    </>
  );
};
